import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Calendar,
  Target,
  ChevronLeft,
  Share2,
  HeartHandshake,
  BookOpen,
  Building,
  DollarSign,
  Star,
  Book,
  GraduationCap,
  Globe,
  Sparkles,
  Loader2,
  AlertCircle,
  Github,
  CheckCircle2,
  User,
  Mail,
  Box,
  Edit,
} from "lucide-react";
import axios from "axios";

const UserContributions = ({ campaign }) => {
  if (!campaign || !campaign.donations || campaign.donations.length === 0) {
    return null;
  }

  return (
    <div className="bg-white rounded-xl shadow-sm p-6 mt-6">
      <h3 className="text-xl font-semibold text-gray-900 mb-6">
        Recent Contributions
      </h3>
      <div className="space-y-4">
        {campaign.donations.map(
          (donation, index) =>
            donation.status === "completed" && (
              <div
                key={index}
                className="flex items-center justify-between p-4 rounded-lg bg-gray-50"
              >
                <div className="flex items-center space-x-4">
                  {/* Icon Section */}
                  <div
                    className={`p-2 rounded-full ${
                      donation.type === "money"
                        ? "bg-green-100"
                        : donation.type === "skills"
                        ? "bg-blue-100"
                        : "bg-purple-100"
                    }`}
                  >
                    {donation.type === "money" ? (
                      <DollarSign className="h-5 w-5 text-green-600" />
                    ) : donation.type === "skills" ? (
                      <Star className="h-5 w-5 text-blue-600" />
                    ) : (
                      <Box className="h-5 w-5 text-purple-600" />
                    )}
                  </div>

                  {/* Content Section */}
                  <div className="flex-1">
                    <div className="font-medium text-gray-900">
                      {donation.type === "money" && (
                        <span>
                          Contributed ₹
                          {donation.amount?.toLocaleString() || "0"}
                        </span>
                      )}
                      {donation.type === "skills" && (
                        <div>
                          <span className="font-normal text-gray-600">
                            Skills:
                          </span>{" "}
                          {donation.skills?.[0]?.description ||
                            "Skill Contribution"}
                        </div>
                      )}
                      {donation.type === "resource" && (
                        <div>
                          <span className="font-normal text-gray-600">
                            Resource:
                          </span>{" "}
                          {donation.resources?.[0]?.description ||
                            "Resource Contribution"}
                        </div>
                      )}
                    </div>
                    <div className="text-sm text-gray-500 mt-1 flex items-center gap-2">
                      <Calendar className="h-4 w-4" />
                      {new Date(donation.createdAt).toLocaleDateString()}
                    </div>
                  </div>
                </div>
              </div>
            )
        )}
      </div>
    </div>
  );
};

const CampaignDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [campaign, setCampaign] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [activeTab, setActiveTab] = useState("overview");
  const [currentAlumni, setCurrentAlumni] = useState(null);
  const [activeImageIndex, setActiveImageIndex] = useState(0);
  const [isRegistering, setIsRegistering] = useState(false);
  const [donationError, setDonationError] = useState("");

  // Add this effect to fetch current alumni details
  useEffect(() => {
    const fetchCurrentAlumni = async () => {
      try {
        const token = localStorage.getItem("token");
        const response = await axios.get(
          `${process.env.REACT_APP_BASE_URL}/api/alumni/me`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        setCurrentAlumni(response.data);
      } catch (err) {
        console.error("Error fetching alumni details:", err);
      }
    };

    fetchCurrentAlumni();
  }, []);
  const [donationForm, setDonationForm] = useState({
    type: "money",
    amount: "",
    skills: "",
    resources: "",
  });

  useEffect(() => {
    const fetchCampaign = async () => {
      try {
        setLoading(true);
        const token = localStorage.getItem("token");
        const response = await axios.get(
          `${process.env.REACT_APP_BASE_URL}/api/campaigns/${id}`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        setCampaign(response.data);
      } catch (err) {
        setError(
          err.response?.data?.message || "Error fetching campaign details"
        );
      } finally {
        setLoading(false);
      }
    };

    fetchCampaign();
  }, [id]);

  // Inside your CampaignDetails component, update the handleDonationSubmit function:
  const DONATION_TYPES = [
    {
      value: "money",
      icon: DollarSign,
      label: "Money",
      placeholder: "Enter amount",
    },
    {
      value: "skills",
      icon: Star,
      label: "Skills",
      placeholder: "Describe your skills",
    },
    {
      value: "resource", // Changed from 'resources' to match schema
      icon: Book,
      label: "Resources",
      placeholder: "Describe your resources",
    },
  ];
 function loadScript(src) {
    return new Promise((resolve) => {
      const script = document.createElement("script");
      script.src = src;
      script.onload = () => {
        resolve(true);
      };
      script.onerror = () => {
        resolve(false);
      };
      document.body.appendChild(script);
    });
  }
  const getRemainingAmount = () => {
    return campaign.financials.targetAmount - campaign.financials.raisedAmount;
  };

  // Add validation before form submission
  const validateDonationAmount = (amount) => {
    const remainingAmount = getRemainingAmount();
    if (amount > remainingAmount) {
      setDonationError(`You cannot donate more than the remaining amount needed (₹${remainingAmount.toLocaleString()})`);
      return false;
    }
    setDonationError("");
    return true;
  };
  const handleDonationSubmit = async (e) => {
    e.preventDefault();
    console.log("Processing registration...");
 
    try {
      const token = localStorage.getItem("token");
      if (donationForm.type === "money") {
        const amount = Number(donationForm.amount);
        if (!validateDonationAmount(amount)) {
          return;
        }
      }
      setIsRegistering(true);
      let donationPayload = {
        type:
          donationForm.type === "resources" ? "resource" : donationForm.type, // Convert 'resources' to 'resource'
        amount: donationForm.type === "money" ? Number(donationForm.amount) : 0,
        skills:
          donationForm.type === "skills" ? { skill: donationForm.skills } : {},
        resources:
          donationForm.type === "resources"
            ? [
                {
                  type: "other",
                  description: donationForm.resources,
                  name: "Custom Resource",
                },
              ]
            : [],
        campaignId: id,
        campaignTitle: campaign.title,
      };

      // For skills or resources donations, directly call donate API without payment flow
      console.log("donationPayload",donationForm);
      if (donationForm.type === "skills" || donationForm.type === "resource") {
        const donateResponse = await fetch(
          `${process.env.REACT_APP_BASE_URL}/api/campaigns/${id}/donate`,
          {
            method: "POST",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              type: donationForm.type,
              amount:
                donationForm.type === "money" ? Number(donationForm.amount) : 0,
              skills:
                donationForm.type === "skills"
                  ? { skill: donationForm.skills }
                  : {},
              resources:
                donationForm.type === "resource"
                  ? [
                      {
                        type: "other",
                        description: donationForm.resources,
                        name: "Custom Resource",
                      },
                    ]
                  : [],
              campaignId: id,
              campaignTitle: campaign.title,
            }),
          }
        );
        // console.log("donateResponse", donateResponse);

        if (!donateResponse.ok) {
          throw new Error("Failed to submit donation");
        }

        const response = await axios.get(
          `${process.env.REACT_APP_BASE_URL}/api/campaigns/${id}`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        setCampaign(response.data);
        setDonationForm({
          type: "money",
          amount: "",
          skills: "",
          resources: "",
        });
        alert("Donation submitted successfully!");
        setIsRegistering(false);
        return;
      }

      // For money donations, proceed with payment flow
      const res = await loadScript(
        "https://checkout.razorpay.com/v1/checkout.js"
      );

      if (!res) {
        console.log(
          "Payment gateway failed to load. Please check your internet connection."
        );
        return;
      }

      // Get payment order from backend
      const orderResponse = await fetch(
        `${process.env.REACT_APP_BASE_URL}/api/campaigns/${id}/donate`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            type: donationForm.type,
            amount:
              donationForm.type === "money" ? Number(donationForm.amount) : 0,
            skills:
              donationForm.type === "skills"
                ? { skill: donationForm.skills }
                : {},
            resources:
              donationForm.type === "resources"
                ? [
                    {
                      type: "other",
                      description: donationForm.resources,
                      name: "Custom Resource",
                    },
                  ]
                : [],
            campaignId: id,
            campaignTitle: campaign.title,
          }),
        }
      );

      if (!orderResponse.ok) {
        throw new Error("Failed to initialize payment");
      }

      const orderData = await orderResponse.json();
      console.log("orderData", orderData);

      // Configure Razorpay
      const options = {
        key: "rzp_test_LkIEjsDE6zd6Cx",
        currency: orderData.paymentResponse.currency,
        amount: orderData.paymentResponse.amount,
        order_id: orderData?.paymentResponse?.id,
        name: "Alumni Campaign Donation",
        description: `Donation for ${campaign.title}`,
        image: "https://i.ibb.co/JB1mSr9/SAARTHI-removebg-preview.png",
        handler: async function (response) {
          // Verify payment with backend
          console.log("Payment response:", response);

          try {
            // Show verifying payment loader
            setIsRegistering(true);
            const verifyResponse = await fetch(
              `${process.env.REACT_APP_BASE_URL}/api/campaigns/${id}/verify-donation`,
              {
                method: "POST",
                headers: {
                  Authorization: `Bearer ${token}`,
                  "Content-Type": "application/json",
                },
                body: JSON.stringify({
                  razorpay_payment_id: response.razorpay_payment_id,
                  razorpay_signature: response.razorpay_signature,
                  razorpay_order_id: response.razorpay_order_id,
                }),
              }
            );

            console.log("verifyResponse", verifyResponse);

            if (verifyResponse.ok) {
              console.log(
                "Payment successful! You are now registered for the event."
              );
              const response = await axios.get(
                `${process.env.REACT_APP_BASE_URL}/api/campaigns/${id}`,
                {
                  headers: { Authorization: `Bearer ${token}` },
                }
              );
              setCampaign(response.data);
              setDonationForm({
                type: "money",
                amount: "",
                skills: "",
                resources: "",
              });
              alert(
                "Payment successful! You are now registered for the event."
              );
            } else {
              const errorData = await verifyResponse.json();
              throw new Error(
                errorData.message || "Payment verification failed"
              );
            }
          } catch (error) {
            console.error("Verification error:", error);
            alert("Payment verification failed. Please contact support.");
          } finally {
            setIsRegistering(false);
          }
        },
        modal: {
          ondismiss: function () {
            console.log("Payment modal closed");
            setIsRegistering(false);
          },
        },
        theme: {
          color: "#3B82F6",
        },
      };

      const paymentObject = new window.Razorpay(options);
      paymentObject.open();

      paymentObject.on("payment.failed", function (response) {
        console.log("Payment failed. Please try again.");
        console.error("Payment failed:", response.error);
        setIsRegistering(false);
      });
    } catch (error) {
      console.error("Payment error:", error);
      console.log("Payment failed. Please try again later.");
      setIsRegistering(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="flex flex-col items-center space-y-4">
          <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
          <p className="text-gray-600 animate-pulse">
            Loading campaign details...
          </p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="max-w-md w-full bg-white rounded-xl shadow-sm p-6">
          <div className="flex items-center space-x-3 text-red-600">
            <AlertCircle className="h-5 w-5" />
            <p className="font-medium">{error}</p>
          </div>
          <button
            onClick={() => navigate("/campaigns")}
            className="mt-4 w-full py-2 bg-gray-100 hover:bg-gray-200 rounded-lg
              text-gray-700 flex items-center justify-center"
          >
            <ChevronLeft className="h-4 w-4 mr-2" />
            Back to Campaigns
          </button>
        </div>
      </div>
    );
  }

  if (!campaign) return null;

  const getCategoryStyle = (category) => {
    const styles = {
      startup: {
        gradient: "from-blue-900 via-indigo-900 to-purple-900",
        icon: Sparkles,
        color: "text-blue-500",
        lightBg: "bg-blue-50",
        border: "border-blue-200",
      },
      research: {
        gradient: "from-green-900 via-emerald-900 to-teal-900",
        icon: BookOpen,
        color: "text-green-500",
        lightBg: "bg-green-50",
        border: "border-green-200",
      },
      infrastructure: {
        gradient: "from-orange-900 via-red-900 to-pink-900",
        icon: Building,
        color: "text-orange-500",
        lightBg: "bg-orange-50",
        border: "border-orange-200",
      },
      innovation: {
        gradient: "from-purple-900 via-fuchsia-900 to-pink-900",
        icon: Target,
        color: "text-purple-500",
        lightBg: "bg-purple-50",
        border: "border-purple-200",
      },
    };

    return styles[category] || styles.startup;
  };

  const renderImageGallery = () => (
    <div className="mt-8 space-y-4">
      {/* Main Image Display */}
      {campaign.media?.images?.length > 0 && (
        <div className="aspect-video rounded-xl overflow-hidden bg-gray-100">
          <img
            src={campaign.media.images[activeImageIndex]?.url}
            alt={
              campaign.media.images[activeImageIndex]?.caption || campaign.title
            }
            className="w-full h-full object-cover"
          />
        </div>
      )}

      {/* Thumbnail Navigation */}
      {campaign.media?.images?.length > 1 && (
        <div className="flex gap-4 overflow-x-auto pb-2">
          {campaign.media.images.map((image, index) => (
            <button
              key={index}
              onClick={() => setActiveImageIndex(index)}
              className={`relative flex-shrink-0 w-24 h-24 rounded-lg overflow-hidden 
                ${
                  activeImageIndex === index
                    ? "ring-2 ring-blue-500"
                    : "ring-1 ring-gray-200"
                }`}
            >
              <img
                src={image.url}
                alt={image.caption || `Image ${index + 1}`}
                className="w-full h-full object-cover"
              />
              {image.caption && (
                <div className="absolute bottom-0 left-0 right-0 bg-black/50 p-1">
                  <p className="text-white text-xs truncate">{image.caption}</p>
                </div>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );

  const categoryStyle = getCategoryStyle(campaign.category);
  const CategoryIcon = categoryStyle.icon;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <div className={`relative bg-gradient-to-br ${categoryStyle.gradient}`}>
        <div className="absolute inset-0 bg-grid-white/[0.05] bg-[size:20px_20px]" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent" />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between py-6">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate("/campaigns")}
                className="text-white/90 hover:text-white flex items-center transition-colors"
              >
                <ChevronLeft className="h-5 w-5 mr-1" />
                Back to Campaigns
              </button>

              {/* Add Edit Button - Only show if user is the organizer */}
              {campaign.organizer.organizedBy._id === currentAlumni?._id && (
                <button
                  onClick={() => navigate(`/campaigns/edit/${id}`)}
                  className="flex items-center px-4 py-2 bg-white/10 rounded-lg 
          text-white hover:bg-white/20 transition-colors"
                >
                  <Edit className="h-4 w-4 mr-2" />
                  Edit Campaign
                </button>
              )}
            </div>

            <button
              onClick={() => {
                /* Share functionality */
              }}
              className="text-white/90 hover:text-white flex items-center transition-colors"
            >
              <Share2 className="h-5 w-5 mr-1" />
              Share
            </button>
          </div>

          <div className="py-16 lg:py-24">
            <div className="flex flex-col items-center text-center max-w-3xl mx-auto">
              <div className={`p-3 ${categoryStyle.lightBg} rounded-xl mb-6`}>
                <CategoryIcon className={`h-6 w-6 ${categoryStyle.color}`} />
              </div>

              <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-6">
                {campaign.title}
              </h1>

              <p className="text-lg text-white/90 mb-8">
                {campaign.description.short}
              </p>

              <div className="flex flex-wrap justify-center gap-6">
                <div className="flex items-center text-white/80">
                  <Building className="h-5 w-5 mr-2" />
                  <span>{campaign?.college?.name || "KSV"}</span>
                </div>
                <div className="flex items-center text-white/80">
                  <Calendar className="h-5 w-5 mr-2" />
                  <span>
                    {new Date(campaign.timeline.startDate).toLocaleDateString()}
                  </span>
                </div>
                <div className="flex items-center text-white/80">
                  <User className="h-5 w-5 mr-2" />
                  <span>
                    {campaign.organizer.organizedBy.firstName}{" "}
                    {campaign.organizer.organizedBy.lastName}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Progress Stats Bar */}
      <div className="relative -mt-12">
        <div className="max-w-4xl mx-auto px-4">
          <div className="bg-white rounded-xl shadow-lg p-6 md:p-8">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              <div className="text-center">
                <div className="text-2xl font-bold text-gray-900">
                  ₹{campaign.financials.targetAmount.toLocaleString()}
                </div>
                <p className="text-sm text-gray-600">Target Amount</p>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-blue-600">
                  ₹{campaign.financials.raisedAmount.toLocaleString()}
                </div>
                <p className="text-sm text-gray-600">Raised So Far</p>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-green-600">
                  {campaign.donations?.length || 0}
                </div>
                <p className="text-sm text-gray-600">Supporters</p>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-purple-600">
                  {campaign.timeline.milestones?.length || 0}
                </div>
                <p className="text-sm text-gray-600">Milestones</p>
              </div>
            </div>

            {/* Progress Bar */}
            <div className="mt-8 space-y-2">
              <div className="flex justify-between text-sm">
                <span className="font-medium text-gray-700">
                  Campaign Progress
                </span>
                <span className="font-medium text-blue-600">
                  {(
                    (campaign.financials.raisedAmount /
                      campaign.financials.targetAmount) *
                    100
                  ).toFixed(1)}
                  %
                </span>
              </div>
              <div className="h-3 bg-gray-100 rounded-full overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-blue-500 to-blue-600 rounded-full transition-all duration-500"
                  style={{
                    width: `${Math.min(
                      (campaign.financials.raisedAmount /
                        campaign.financials.targetAmount) *
                        100,
                      100
                    )}%`,
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left Column - Main Content */}
          <div className="lg:col-span-2 space-y-8">
            {/* Navigation Tabs */}
            <div className="bg-white rounded-xl shadow-sm">
              <div className="flex border-b">
                {["overview", "milestones", "budget", ].map(
                  (tab) => (
                    <button
                      key={tab}
                      onClick={() => setActiveTab(tab)}
                      className={`flex-1 px-4 py-3 text-sm font-medium transition-colors
                      ${
                        activeTab === tab
                          ? "text-blue-600 border-b-2 border-blue-600"
                          : "text-gray-600 hover:text-gray-900"
                      }`}
                    >
                      {tab.charAt(0).toUpperCase() + tab.slice(1)}
                    </button>
                  )
                )}
              </div>

              {/* Tab Content */}
              <div className="p-6">
                {activeTab === "overview" && (
                  <div className="space-y-8">
                    {/* Image Gallery */}
                    {renderImageGallery()}
                    {/* Description */}
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-4">
                        About This Campaign
                      </h3>
                      <p className="text-gray-600">
                        {campaign.description.detailed}
                      </p>
                    </div>

                    {/* Impact Section */}
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-4">
                        Impact & Outcomes
                      </h3>
                      <div className="grid sm:grid-cols-2 gap-4">
                        <div
                          className={`p-4 rounded-lg ${categoryStyle.lightBg} ${categoryStyle.border}`}
                        >
                          <h4 className="font-medium text-gray-900 mb-2">
                            Beneficiaries
                          </h4>
                          <div className="flex flex-wrap gap-2">
                            {campaign.impact.beneficiaries.map(
                              (item, index) => (
                                <span
                                  key={index}
                                  className="px-2 py-1 bg-white rounded text-sm text-gray-700"
                                >
                                  {item}
                                </span>
                              )
                            )}
                          </div>
                        </div>
                        <div
                          className={`p-4 rounded-lg ${categoryStyle.lightBg} ${categoryStyle.border}`}
                        >
                          <h4 className="font-medium text-gray-900 mb-2">
                            Expected Outcomes
                          </h4>
                          <ul className="space-y-2 text-sm text-gray-700">
                            {campaign.impact.outcomes.map((outcome, index) => (
                              <li key={index} className="flex items-start">
                                <CheckCircle2 className="h-4 w-4 mr-2 mt-0.5 text-green-500" />
                                {outcome}
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    </div>

                    {/* Campaign Links */}
                    {(campaign.projectLinks.demo ||
                      campaign.projectLinks.github ||
                      campaign.projectLinks.driveFolder) && (
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900 mb-4">
                          Project Resources
                        </h3>
                        <div className="grid sm:grid-cols-2 gap-4">
                          {campaign.projectLinks.demo && (
                            <a
                              href={campaign.projectLinks.demo}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="flex items-center p-4 rounded-lg border hover:bg-gray-50 transition-colors"
                            >
                              <Globe className="h-5 w-5 text-blue-500 mr-3" />
                              <div>
                                <div className="font-medium text-gray-900">
                                  Demo Link
                                </div>
                                <div className="text-sm text-gray-500">
                                  View live demo
                                </div>
                              </div>
                            </a>
                          )}
                          {campaign.projectLinks.github && (
                            <a
                              href={campaign.projectLinks.github}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="flex items-center p-4 rounded-lg border hover:bg-gray-50 transition-colors"
                            >
                              <Github className="h-5 w-5 text-gray-900 mr-3" />
                              <div>
                                <div className="font-medium text-gray-900">
                                  Source Code
                                </div>
                                <div className="text-sm text-gray-500">
                                  GitHub repository
                                </div>
                              </div>
                            </a>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {activeTab === "milestones" && (
                  <div className="space-y-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">
                      Project Timeline
                    </h3>
                    <div className="relative">
                      {campaign.timeline.milestones.map((milestone, index) => (
                        <div key={index} className="flex gap-4 mb-8 last:mb-0">
                          <div className="flex flex-col items-center">
                            <div className="w-8 h-8 rounded-full bg-blue-100 border-2 border-blue-500 flex items-center justify-center">
                              {index + 1}
                            </div>
                            {index <
                              campaign.timeline.milestones.length - 1 && (
                              <div className="w-0.5 h-full bg-blue-200" />
                            )}
                          </div>
                          <div className="flex-1 pb-8">
                            <div className="bg-white rounded-lg border p-4">
                              <h4 className="font-medium text-gray-900 mb-2">
                                {milestone.title}
                              </h4>
                              <p className="text-gray-600 text-sm mb-3">
                                {milestone.description}
                              </p>
                              <div className="flex items-center text-sm text-gray-500">
                                <Calendar className="h-4 w-4 mr-1" />
                                {new Date(
                                  milestone.targetDate
                                ).toLocaleDateString()}
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {activeTab === "budget" && (
                  <div className="space-y-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">
                      Budget Breakdown
                    </h3>
                    <div className="space-y-4">
                      {campaign.financials.budget.map((item, index) => (
                        <div
                          key={index}
                          className="bg-white rounded-lg border p-4"
                        >
                          <div className="flex justify-between items-start">
                            <div>
                              <h4 className="font-medium text-gray-900">
                                {item.category}
                              </h4>
                              <p className="text-sm text-gray-600 mt-1">
                                {item.description}
                              </p>
                            </div>
                            <div className="text-lg font-semibold text-gray-900">
                              ₹{item.amount.toLocaleString()}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {activeTab === "technical" && campaign.technicalDetails && (
                  <div className="space-y-8">
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-4">
                        Technical Stack
                      </h3>
                      <div className="flex flex-wrap gap-2">
                        {campaign.technicalDetails.stack.map((tech, index) => (
                          <span
                            key={index}
                            className="px-3 py-1 bg-gray-100 rounded-full text-sm text-gray-700"
                          >
                            {tech}
                          </span>
                        ))}
                      </div>
                    </div>

                    {campaign.technicalDetails.architecture && (
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900 mb-4">
                          Architecture Overview
                        </h3>
                        <p className="text-gray-600">
                          {campaign.technicalDetails.architecture}
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Right Column - Contribution Form & Info */}
          <div className="space-y-6">
            {/* Contribution Form */}
            <div className="bg-white rounded-xl shadow-sm p-6 sticky top-6">
              <h3 className="text-xl font-semibold text-gray-900 mb-6">
                Support This Campaign
              </h3>
              <form onSubmit={handleDonationSubmit} className="space-y-6">
                <div className="grid grid-cols-3 gap-3">
                  {DONATION_TYPES.map((type) => (
                    <button
                      key={type.value}
                      type="button"
                      onClick={() =>
                        setDonationForm((prev) => ({
                          ...prev,
                          type: type.value,
                        }))
                      }
                      className={`p-4 rounded-lg border-2 transition-all
        ${
          donationForm.type === type.value
            ? `${categoryStyle.border} ${categoryStyle.lightBg}`
            : "border-gray-200 hover:border-gray-300"
        }`}
                    >
                      <type.icon
                        className={`h-5 w-5 mx-auto ${
                          donationForm.type === type.value
                            ? categoryStyle.color
                            : "text-gray-400"
                        }`}
                      />
                      <div className="text-xs text-center mt-2">
                        {type.label}
                      </div>
                    </button>
                  ))}
                </div>

                {/* Dynamic Form Fields */}
                <div className="space-y-4">
          {donationForm.type === "money" ? (
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">
                Amount (₹)
              </label>
              <div className="relative">
                <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input
                  type="number"
                  value={donationForm.amount}
                  onChange={(e) => {
                    setDonationForm((prev) => ({
                      ...prev,
                      amount: e.target.value,
                    }));
                    // Clear error when user starts typing
                    setDonationError("");
                  }}
                  className={`pl-10 w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 
                    ${donationError ? 'border-red-300' : 'border-gray-200'}`}
                  placeholder="Enter amount"
                  required
                  min="1"
                  max={getRemainingAmount()}
                />
              </div>
              {/* Show remaining amount needed */}
              <div className="text-sm text-gray-600 mt-1">
                Remaining amount needed: ₹{getRemainingAmount().toLocaleString()}
              </div>
              {/* Error message */}
              {donationError && (
                <div className="text-sm text-red-600 mt-1">
                  {donationError}
                </div>
              )}
            </div>
          ) : (
                    <div className="space-y-2">
                      <label className="block text-sm font-medium text-gray-700">
                        {donationForm.type === "skills"
                          ? "Skills Offered"
                          : "Resources Offered"}
                      </label>
                      <textarea
                        value={
                          donationForm.type === "skills"
                            ? donationForm.skills
                            : donationForm.resources
                        }
                        onChange={(e) =>
                          setDonationForm((prev) => ({
                            ...prev,
                            [donationForm.type === "resource"
                              ? "resources"
                              : donationForm.type]: e.target.value,
                          }))
                        }
                        className="w-full p-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500"
                        rows={4}
                        placeholder={`Describe your ${
                          donationForm.type === "resource"
                            ? "resources"
                            : donationForm.type
                        }...`}
                        required
                      />
                    </div>
                  )}
                </div>

                <button
          type="submit"
          className={`w-full flex items-center justify-center p-3 bg-gradient-to-r ${categoryStyle.gradient}
            text-white rounded-lg hover:opacity-90 transition-opacity 
            ${isRegistering ? 'opacity-50 cursor-not-allowed' : ''}`}
          disabled={isRegistering || !!donationError}
        >
          <HeartHandshake className="h-5 w-5 mr-2" />
          {isRegistering ? "Processing..." : "Support Now"}
        </button>
              </form>
            </div>
            <UserContributions campaign={campaign} />
            {/* Organizer Info */}
            <div className="bg-white rounded-xl shadow-sm p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                About the Organizer
              </h3>
              <div className="flex items-center space-x-4 mb-6">
                <img
                  src={campaign?.organizer?.organizedBy?.profileImage || campaign?.organizer?.organizedBy?.personalInfo?.avatar}
                  alt={campaign.organizer.organizedBy.firstName}
                  className="h-16 w-16 rounded-full object-cover"
                />
                <div>
                  <h4 className="font-medium text-gray-900">
                    {campaign.organizer.organizedBy.firstName}{" "}
                    {campaign.organizer.organizedBy.lastName}
                  </h4>
                  <p className="text-sm text-gray-500">
                    {campaign.organizer.type}
                  </p>
                </div>
              </div>

              <div className="space-y-4 text-sm text-gray-600">
                <div className="flex items-center">
                  <Building className="h-4 w-4 mr-2" />
                  <span>{campaign?.college?.name || "KSV"}</span>
                </div>
                <div className="flex items-center">
                  <GraduationCap className="h-4 w-4 mr-2" />
                  <span>{campaign.organizer.organizedBy.department}</span>
                </div>
                <div className="flex items-center">
                  <Mail className="h-4 w-4 mr-2" />
                  <span>
                    {
                      campaign.organizer.organizedBy.personalInfo
                        ?.alternateEmail
                    }
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CampaignDetails;
